import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import {
  // eslint-disable-next-line no-restricted-imports
  useLazyQuery as apolloUseLazyQuery,
} from '@apollo/client/react';
import { useCallback } from 'react';

// `returnPartialData` and `@defer` are unused, so `dataState` can only ever be
// 'complete' or 'empty'. Narrowing the union here keeps `data` as
// `TData | undefined` for callers instead of also admitting `DeepPartial<TData>`.
export type CompleteLazyResult<
  TData,
  TVariables extends OperationVariables,
> = apolloUseLazyQuery.Result<TData, TVariables, 'complete' | 'empty'>;

// Unlike apollo's, `options` is always optional: the hook merges its own
// `variables` in, so a call with none can still be fully specified.
export type LazyQueryExecFunction<
  TData,
  TVariables extends OperationVariables,
> = (
  options?: apolloUseLazyQuery.ExecOptions<TVariables>
) => ReturnType<apolloUseLazyQuery.ExecFunction<TData, TVariables>>;

type Options<
  TData,
  TVariables extends OperationVariables,
> = apolloUseLazyQuery.Options<NoInfer<TData>, NoInfer<TVariables>> & {
  variables?: Partial<NoInfer<TVariables>>;
};

/**
 * @param options - `variables` here are defaults, merged into the ones passed to
 * the returned execute function.
 */
export const useLazyQuery = <TData, TVariables extends OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: Options<TData, TVariables>
): [
  LazyQueryExecFunction<TData, TVariables>,
  CompleteLazyResult<TData, TVariables>,
] => {
  const { variables: defaultVariables, ...apolloOptions } = options ?? {};

  // v4 replaces the query's variables with whatever `execute` is called with,
  // instead of merging them over the hook's. Key the merge on the defaults' value
  // rather than their identity, so the returned function stays referentially
  // stable across renders for effect dependency arrays.
  const defaultVariablesKey = JSON.stringify(defaultVariables ?? null);

  const [execute, result] = apolloUseLazyQuery(
    query,
    apolloOptions as apolloUseLazyQuery.Options<
      NoInfer<TData>,
      NoInfer<TVariables>
    >
  );

  /* oxlint-disable react-hooks/exhaustive-deps -- keyed on the defaults' value;
     depending on the object itself would change every render */
  const executeWithDefaults = useCallback(
    // apollo returns a decorated promise carrying extra handles, and awaiting
    // here would strip them
    // oxlint-disable-next-line typescript/promise-function-async
    (...[execOptions]: Parameters<LazyQueryExecFunction<TData, TVariables>>) =>
      execute(
        ...([
          {
            ...execOptions,
            variables: {
              ...defaultVariables,
              ...execOptions?.variables,
            },
          },
        ] as Parameters<typeof execute>)
      ),
    [execute, defaultVariablesKey]
  ) as LazyQueryExecFunction<TData, TVariables>;
  /* oxlint-enable react-hooks/exhaustive-deps */

  return [executeWithDefaults, result as CompleteLazyResult<TData, TVariables>];
};
